import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DashboardHeader, AthleteSearchFilter, AthletesGrid } from '../components/dashboard';
import TeamManager from '../components/TeamManager';
import UserManagement from '../components/UserManagement';
import PartnerManagement from '../components/PartnerManagement';
import PartnerBranding from '../components/PartnerBranding';
import PartnerDashboardContent from '../components/PartnerDashboardContent';
import ApiKeyManagement from '../components/ApiKeyManagement';
import TeamAggregateDashboard from '../components/TeamAggregateDashboard';
import CoachBioModal from '../components/CoachBioModal';
import BulkDownloadReports from '../components/BulkDownloadReports';
import AppTutorial from '../components/AppTutorial';
import ReferralsDashboard from '../components/ReferralsDashboard';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import ParentEngagementDashboard from '../components/ParentEngagementDashboard';
import RegradeAllTestsButton from '../components/RegradeAllTestsButton';
import BulkGenerateDossiersButton from '../components/BulkGenerateDossiersButton';
// New feature components
import CommunicationHub from '../components/CommunicationHub';
import AttendanceTracker from '../components/AttendanceTracker';
import InjuryTracker from '../components/InjuryTracker';
import PerformanceComparison from '../components/PerformanceComparison';
import WeatherTraining from '../components/WeatherTraining';
import PracticePlanBuilder from '../components/PracticePlanBuilder';
import ParentApprovalDashboard from '../components/ParentApprovalDashboard';
import { Button } from '@/components/ui/button';
import { User, ArrowRight, X } from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [children, setChildren] = useState([]);
  const [childrenData, setChildrenData] = useState({});
  const [medicalStatuses, setMedicalStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertSummary, setAlertSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterCoach, setFilterCoach] = useState('');
  const [showCoachBio, setShowCoachBio] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check onboarding status for athletes/parents
  const checkOnboardingStatus = useCallback(async () => {
    if (user?.role === 'athlete' || user?.role === 'parent') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOnboardingStatus(response.data);
      } catch (err) {
        console.error('Failed to check onboarding status:', err);
      }
    }
  }, [user]);

  // Check if athlete needs to complete profile (first login prompt)
  const checkProfileCompletion = useCallback(async () => {
    if (user?.role === 'athlete' || user?.role === 'child') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/children/my-profile/completion-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.needs_completion) {
          setProfileCompletion(response.data);
        }
      } catch (err) {
        console.error('Failed to check profile completion:', err);
      }
    }
  }, [user]);

  const loadAlertSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts/summary`);
      setAlertSummary(response.data);
    } catch (error) {
      console.error('Failed to load alert summary');
    }
  }, []);

  const loadChildren = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/children`, { headers });
      setChildren(response.data);
      
      // Get child IDs for batch queries
      const childIds = response.data.map(c => c.id);
      
      // Batch load measurements and medical statuses in parallel (single API call each)
      const [measurementsRes, medicalData] = await Promise.all([
        // Batch load measurements (single API call)
        childIds.length > 0
          ? axios.post(`${API}/children/measurements/batch`, childIds, { headers }).catch(() => ({ data: { measurements: {} } }))
          : Promise.resolve({ data: { measurements: {} } }),
        // Batch load medical statuses (single API call)
        childIds.length > 0 
          ? axios.post(`${API}/medical/status/batch`, childIds, { headers }).catch(() => ({ data: { statuses: {} } }))
          : Promise.resolve({ data: { statuses: {} } })
      ]);
      
      // Set measurements data directly from batch response
      setChildrenData(measurementsRes.data?.measurements || {});
      
      // Set medical statuses
      setMedicalStatuses(medicalData.data?.statuses || {});
    } catch (error) {
      toast.error('Failed to load children');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChildren();
    loadAlertSummary();
    checkOnboardingStatus();
    checkProfileCompletion();
  }, [loadChildren, loadAlertSummary, checkOnboardingStatus, checkProfileCompletion]);

  // Get unique teams and coaches for filters
  const { teams, coaches } = useMemo(() => {
    const teamsSet = new Set();
    const coachesSet = new Set();
    children.forEach(child => {
      if (child.current_team_name) teamsSet.add(child.current_team_name);
      if (child.assigned_coach_name) coachesSet.add(child.assigned_coach_name);
    });
    return {
      teams: Array.from(teamsSet).sort(),
      coaches: Array.from(coachesSet).sort()
    };
  }, [children]);

  // Filter children based on search and filters
  const filteredChildren = useMemo(() => {
    return children.filter(child => {
      const fullName = `${child.first_name} ${child.last_name}`.toLowerCase();
      const matchesSearch = !searchQuery || fullName.includes(searchQuery.toLowerCase());
      const matchesTeam = !filterTeam || child.current_team_name === filterTeam;
      const matchesCoach = !filterCoach || child.assigned_coach_name === filterCoach;
      return matchesSearch && matchesTeam && matchesCoach;
    });
  }, [children, searchQuery, filterTeam, filterCoach]);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterTeam('');
    setFilterCoach('');
  };

  const handleDeleteChild = (childId) => {
    setChildren(prev => prev.filter(c => c.id !== childId));
    // Also remove from childrenData
    setChildrenData(prev => {
      const newData = { ...prev };
      delete newData[childId];
      return newData;
    });
  };

  const hasActiveFilters = searchQuery || filterTeam || filterCoach;

  // Show Partner Dashboard for partners
  if (user?.role === 'partner') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
        <DashboardHeader
          alertSummary={alertSummary}
          onShowCoachBio={() => setShowCoachBio(true)}
          onRefresh={loadChildren}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <PartnerDashboardContent />
        </div>
        {showCoachBio && <CoachBioModal onClose={() => setShowCoachBio(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
      {/* Profile Completion Modal - shown on first login for athletes */}
      {profileCompletion && (
        <ProfileCompletionModal
          childName={profileCompletion.child_name}
          missingFields={profileCompletion.missing_fields}
          onComplete={() => setProfileCompletion(null)}
          onSkip={() => setProfileCompletion(null)}
        />
      )}

      {/* Header */}
      <DashboardHeader
        alertSummary={alertSummary}
        onShowCoachBio={() => setShowCoachBio(true)}
        onRefresh={loadChildren}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Onboarding Reminder Banner */}
        {onboardingStatus && !onboardingStatus.completed && showOnboardingBanner && (
          <div 
            className="mb-6 bg-gradient-to-r from-[#02b5ff]/20 to-purple-500/20 border border-[#02b5ff]/30 rounded-xl p-4 relative"
            data-testid="onboarding-banner"
          >
            <button
              onClick={() => setShowOnboardingBanner(false)}
              className="absolute top-3 right-3 text-zinc-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#02b5ff]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-6 w-6 text-[#02b5ff]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold">Complete Your Profile</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  Finish setting up your account to unlock all features and get personalized recommendations.
                </p>
              </div>
              <Button
                onClick={() => navigate('/onboarding')}
                className="bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white flex-shrink-0"
                data-testid="complete-onboarding-btn"
              >
                Complete Setup
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#02b5ff] transition-all"
                  style={{ width: `${(onboardingStatus.current_step / 5) * 100}%` }}
                />
              </div>
              <span className="text-xs text-zinc-500">
                {onboardingStatus.current_step}/5 steps
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-zinc-500 py-20" data-testid="loading-state">
            <div className="inline-flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-[#02b5ff] border-t-transparent rounded-full animate-spin" />
              <span>Loading athletes...</span>
            </div>
          </div>
        ) : children.length === 0 ? (
          <AthletesGrid
            athletes={[]}
            childrenData={{}}
            medicalStatuses={{}}
            hasActiveFilters={false}
            onClearFilters={clearFilters}
            onDelete={handleDeleteChild}
            onRefresh={loadChildren}
          />
        ) : (
          <div>
            {/* Search and Filter Section */}
            <AthleteSearchFilter
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterTeam={filterTeam}
              setFilterTeam={setFilterTeam}
              filterCoach={filterCoach}
              setFilterCoach={setFilterCoach}
              teams={teams}
              coaches={coaches}
              filteredCount={filteredChildren.length}
              totalCount={children.length}
            />
            
            {/* Athletes Grid */}
            <AthletesGrid
              athletes={filteredChildren}
              childrenData={childrenData}
              medicalStatuses={medicalStatuses}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              onDelete={handleDeleteChild}
              onRefresh={loadChildren}
            />
            
            {/* Bulk Download Reports - for coaches and team managers */}
            {(user?.role === 'coach' || user?.role === 'team_manager') && filteredChildren.length > 0 && (
              <div className="mt-6 flex justify-end">
                <BulkDownloadReports 
                  teamName={filterCoach || undefined}
                  className="shadow-lg"
                />
              </div>
            )}
            
            {/* Team Aggregate Dashboard - for coaches and admins */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8">
                <TeamAggregateDashboard teamName={filterCoach || undefined} />
              </div>
            )}
            
            {/* Team Manager Section */}
            {(user?.role === 'coach' || user?.role === 'admin') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <TeamManager onTeamSelect={(team) => navigate(`/coach-dashboard?team=${team.id}`)} />
              </div>
            )}
            
            {/* User Management Section - for EPP Staff to manage all users */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <BulkGenerateDossiersButton />
              </div>
            )}

            {/* User Management Section - for EPP Staff to manage all users */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <RegradeAllTestsButton />
              </div>
            )}

            {/* User Management Section - for EPP Staff to manage all users */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <UserManagement />
              </div>
            )}
            
            {/* Partner Management Section - for EPP Staff to manage partner organizations */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <PartnerManagement />
              </div>
            )}
            
            {/* Referrals Dashboard - for EPP Staff to manage incoming referrals */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8">
                <ReferralsDashboard />
              </div>
            )}

            {/* Parent Engagement Dashboard - for coaches */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff' || user?.role === 'team_manager') && (
              <div className="mt-8">
                <ParentEngagementDashboard />
              </div>
            )}
            
            {/* Developer API Section - for EPP Staff to manage API keys */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <ApiKeyManagement />
              </div>
            )}
            
            {/* Partner Branding Section - for Partners to customize their branding */}
            {user?.role === 'partner' && (
              <div className="mt-8 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                <PartnerBranding />
              </div>
            )}

            {/* New Features Section */}
            {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff' || user?.role === 'team_manager') && (
              <>
                {/* Communication & Team Tools Row */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <CommunicationHub user={user} />
                  <WeatherTraining />
                </div>

                {/* Attendance & Injury Row */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <AttendanceTracker teamId={filterTeam || teams[0]} user={user} />
                  <InjuryTracker teamId={filterTeam || teams[0]} user={user} />
                </div>

                {/* Performance & Practice Row */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <PerformanceComparison athletes={children} teamId={filterTeam || teams[0]} />
                  <PracticePlanBuilder teamId={filterTeam || teams[0]} />
                </div>

                {/* Parent Approval Dashboard - for EPP Staff */}
                {(user?.role === 'coach' || user?.role === 'admin' || user?.role === 'epp_staff') && (
                  <div className="mt-6 bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6">
                    <ParentApprovalDashboard />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Coach Bio Modal */}
      <CoachBioModal
        coach={{ id: user?.id, full_name: user?.full_name }}
        isOpen={showCoachBio}
        onClose={() => setShowCoachBio(false)}
        isOwnProfile={true}
      />
      
      {/* App Tutorial - only for athletes and parents */}
      {(user?.role === 'athlete' || user?.role === 'parent') && (
        <AppTutorial />
      )}
    </div>
  );
}
